/**
 * Resolve a Drop's reveal moment to a real UTC `Date` instant.
 *
 * - When `reveal_tz` is present (new drops), `reveal_date` + `reveal_time` are
 *   interpreted as wall-clock time IN that IANA timezone, so the resulting
 *   `Date` represents the exact same real-world moment everywhere (the cron
 *   on the server and the countdown in any browser will agree).
 * - When `reveal_tz` is NULL (legacy rows, 19 historical drops), fall back to
 *   `new Date("YYYY-MM-DDTHH:mm")` which JS parses as runtime-local time —
 *   matches the pre-fix behavior so nothing regresses for old data.
 *
 * Pure DOM/Deno-friendly: only uses built-in `Intl.DateTimeFormat`, no deps.
 */
export function getRevealDateTime(
  reveal_date: string,
  reveal_time: string,
  reveal_tz?: string | null,
): Date {
  if (!reveal_tz) {
    // Legacy path — preserve pre-fix behavior for rows created before reveal_tz existed.
    return new Date(`${reveal_date}T${reveal_time}`);
  }
  return zonedWallClockToUtc(reveal_date, reveal_time, reveal_tz);
}

/**
 * Convert a wall-clock date/time string in a given IANA timezone to the
 * corresponding UTC `Date` instant.
 *
 * Algorithm (the standard "offset trick"):
 *   1. Treat the wall-clock components as if they were already UTC ("guess").
 *   2. Ask Intl what that guess instant looks like when rendered in `tz`.
 *   3. The delta between (rendered-in-tz) and (guess) is the tz's UTC offset
 *      at that moment, so subtracting it gives the true UTC instant.
 *
 * Accurate to the second outside DST-transition ambiguity (where one wall-clock
 * hour either does not exist or exists twice; we accept the standard-time
 * resolution in those rare cases).
 */
function zonedWallClockToUtc(
  dateStr: string,
  timeStr: string,
  tz: string,
): Date {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = timeStr.split(":").map(Number);

  const guessUtcMs = Date.UTC(y, (mo || 1) - 1, d || 1, hh, mm, ss);

  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = Object.fromEntries(
    dtf
      .formatToParts(new Date(guessUtcMs))
      .filter((p) => p.type !== "literal")
      .map((p) => [p.type, p.value]),
  ) as Record<string, string>;

  // Intl can render hour as "24" at midnight in some locales — normalize.
  const renderedHour = Number(parts.hour) === 24 ? 0 : Number(parts.hour);

  const renderedAsUtcMs = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    renderedHour,
    Number(parts.minute),
    Number(parts.second),
  );

  const offsetMs = renderedAsUtcMs - guessUtcMs;
  return new Date(guessUtcMs - offsetMs);
}
